/**
 * Quando as faixas do Kanban cabem todas à vista e quando passam a deslizar.
 *
 * É uma regra só, num lugar só, porque o tabuleiro e o sandbox de desenvolvimento
 * precisam concordar — e porque o breakpoint do Vuetify não serve para decidir isso.
 * Duas razões:
 *
 * 1. O `sm` (600px) não sabe QUANTAS faixas existem. Um fluxo de três situações cabe
 *    lado a lado bem antes de um de seis, e a 960px com seis faixas cada uma fica com
 *    160px — estreita demais para o card.
 * 2. O tabuleiro também vive dentro do modal do Kanban, onde o tamanho da janela não
 *    diz nada sobre o espaço que ele tem. É o mesmo motivo pelo qual o ClassDiagram
 *    observa o próprio elemento em vez de ler o breakpoint.
 */

/**
 * Abaixo disto uma faixa não comporta um card legível.
 *
 * O número sai da cadeia de recuos entre a largura da faixa e a grade de campos do
 * card: 24px de gutter da coluna, 16px da lista da faixa, 2px de borda do card e 16px
 * do recuo da grade — 58px no total. Com 200px de faixa sobram 142px de grade, ainda
 * acima dos 128px para os quais ela foi desenhada; o piso é por volta de 190px, e
 * abaixo dele o `min()` da grade a faz encolher em vez de transbordar, mas o card
 * passa a dizer cada vez menos.
 */
export const MIN_LANE_WIDTH = 200

/**
 * As faixas precisam deslizar?
 *
 * Sempre lado a lado, nunca em duas fileiras e nunca empilhadas: quando a divisão da largura
 * daria menos que o mínimo, elas passam a ter o mínimo e o conjunto desliza, com as setas do
 * `v-slide-group` para andar de um lado a outro.
 *
 * **Empilhar era o comportamento anterior**, e trocá-lo foi decisão de desenho: um Kanban
 * empilhado deixa de ser um Kanban — perde-se a comparação entre as raias, que é o que o quadro
 * existe para dar, e a altura passava a ser a soma de todas. Deslizando, o que se perde é só o
 * que está fora da janela, e um gesto o traz de volta.
 *
 * A conta é a mesma de antes e o valor de retorno tem a mesma polaridade: `true` é o caso
 * apertado. Sem largura medida ainda responde `false` — é o estado de antes da primeira medição,
 * e o tabuleiro só desenha depois de carregar.
 */
export function lanesSlide(availableWidth: number, lanes: number): boolean {
    if (!availableWidth || !lanes) return false
    return availableWidth < lanes * MIN_LANE_WIDTH
}

/**
 * O gutter do grid, que a faixa perde ao sair da linha.
 *
 * O `.v-col` tira o padding dele do `--v-col-gap-x`, e essa variável é declarada pelo `.v-row`:
 * fora de uma linha ela não existe, o `calc` fica inválido e a coluna acaba sem padding nenhum —
 * as faixas colariam uma na outra dentro do `v-slide-group`. 24px é o `$grid-gutter` do Vuetify,
 * o mesmo que a linha declara.
 */
export const LANE_GUTTER = 24

/**
 * As variáveis CSS de que a faixa precisa quando desliza: a largura mínima e o gutter.
 *
 * Vão por `style` em vez de estarem escritas na folha de estilo para o número morar num lugar
 * só — é o MESMO `MIN_LANE_WIDTH` que decide se desliza e que passa a ser a largura de cada
 * faixa, e duas cópias dele fariam a conta e o desenho discordarem na primeira vez que alguém
 * ajustasse uma delas.
 */
export function laneSlideVars(): Record<string, string> {
    return {
        '--ontos-lane-min': `${MIN_LANE_WIDTH}px`,
        '--v-col-gap-x': `${LANE_GUTTER}px`,
        //Vertical ZERO, e não o gutter: na linha o padding de cima e de baixo da coluna é
        //cancelado pela margem negativa do `.v-row`, e o resultado é faixa encostada no
        //cabeçalho do bloco. Fora da linha não há margem negativa que cancele, e o gutter
        //apareceria como um respiro de 12px que o desenho de hoje não tem.
        '--v-col-gap-y': '0px'
    }
}
